import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { ArrowUp } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { TURNSTILE_SITE_KEY, turnstileEnabled } from "@/lib/turnstile";
import { trackRfqFirstMessageSent } from "@/lib/mag-analytics";
import type { QuoteIntakeMessage } from "@/services/quoteIntakeService";

export interface QuoteIntakeChatProps {
  messages: QuoteIntakeMessage[];
  /** True while a turn is in flight — renders the typing indicator. */
  isThinking: boolean;
  /** False once the conversation reaches a draft or is declined. */
  canSend: boolean;
  turnsRemaining: number;
  /**
   * `captchaToken` is present only on the message that OPENS an anonymous
   * conversation — see `requireCaptcha`. Every later turn authenticates with
   * the guest token instead, so there is nothing for a captcha to prove.
   */
  onSend: (content: string, captchaToken?: string) => void;
  /**
   * The next message will mint a guest identity, so it has to carry a captcha
   * (spec b5b59843, control C-6).
   *
   * The parent decides this, because it is the only one that knows both halves:
   * nobody is signed in AND no conversation exists yet. Passing it as a prop
   * rather than reading auth here keeps this component a view.
   *
   * WHY THIS BLOCKS SENDING RATHER THAN DEGRADING: `public/quote-intake`
   * refuses a start without a token whenever the API has
   * TURNSTILE_SECRET_KEY and runs in production, and the refusal is a 400 —
   * not the 503 the static-form fallback listens for. Sending without a token
   * on such an environment is therefore a dead end the visitor cannot read, so
   * the composer waits for the widget instead of letting them discover it.
   */
  requireCaptcha?: boolean;
  /** Opt out into the static title/description form. */
  onUseForm: () => void;
  /**
   * What the visitor already typed before they got here — the landing hero's
   * ask box carries it over as `?q=`.
   *
   * SEEDS THE COMPOSER, never sends. Auto-sending would fire a turn the
   * visitor did not press send on, and `me/quote-intake` is AuthGuard-ed, so
   * for an anonymous arrival from the landing it would fail before they ever
   * saw their own words. Seeding instead means they land on their sentence,
   * already typed, with the send button under their thumb.
   *
   * Applied ONCE, and only to an empty composer on an empty conversation:
   * re-applying it would overwrite what they are mid-way through typing every
   * time this re-renders.
   */
  initialDraft?: string | null;
}

/** Openers offered before the first message, so the consumer never faces a blank box. */
const SUGGESTION_KEYS = [
  "requests.user.quoteIntakeSuggestion1",
  "requests.user.quoteIntakeSuggestion2",
  "requests.user.quoteIntakeSuggestion3",
] as const;

const MAX_MESSAGE_LENGTH = 2000;

/**
 * The conversational RFQ intake (spec add326e0).
 *
 * Turn-by-turn rather than streamed, so a turn arrives whole: the typing
 * indicator is doing real work here, since there is no token-by-token feedback
 * to fill the wait.
 *
 * The greeting and the suggestions are rendered client-side and are NOT part of
 * the transcript — they cost no model turn, and the turn budget is small enough
 * that spending one on "hola" would be a real loss.
 */
export function QuoteIntakeChat({
  messages,
  isThinking,
  canSend,
  turnsRemaining,
  onSend,
  onUseForm,
  initialDraft,
  requireCaptcha = false,
}: QuoteIntakeChatProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [value, setValue] = useState("");
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  /**
   * The message waiting on the challenge, or null when nothing is.
   *
   * ASKED FOR AT THE MOMENT IT IS NEEDED, in a dialog, rather than parked
   * under the composer. The widget there was a box of someone else's chrome
   * sitting between the visitor and the one control on the screen, explaining
   * nothing — and when it failed to load it silently disabled sending, which
   * is exactly how it was first seen on dev.
   *
   * Holding the TEXT rather than a boolean is what makes "verify, then send"
   * one action: the dialog resolves and the message it was opened for goes,
   * with no second press and nothing retyped.
   *
   * `turnstileEnabled` is the same flag the API's rule is mirrored by, so in
   * `vite dev` — and on any build without the site key — the dialog never
   * opens and the composer behaves exactly as it did before this control
   * existed.
   */
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  /** Set when the widget cannot run at all — a blocked or unreachable challenge. */
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const initialDraftAppliedRef = useRef(false);
  const endRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isEmpty = messages.length === 0;
  /**
   * The opening turn arrives instead of being already there.
   *
   * Rendered fully-formed on load, the greeting is easy to skip past without
   * reading — which defeats opening with a question. Showing the typing
   * indicator first and then revealing it makes the eye land on the bubble,
   * the same way a real incoming message does.
   *
   * Starts revealed for a conversation that already has messages, so this only
   * ever plays once, on a fresh chat.
   */
  const [introRevealed, setIntroRevealed] = useState(!isEmpty);

  useEffect(() => {
    if (initialDraftAppliedRef.current) return;
    const seed = initialDraft?.trim();
    if (!seed) return;
    initialDraftAppliedRef.current = true;
    // Only onto a blank slate. Someone resuming a conversation, or already
    // typing, must not have it replaced by a sentence from a previous visit.
    if (!isEmpty) return;
    setValue((current) => (current === "" ? seed.slice(0, MAX_MESSAGE_LENGTH) : current));
  }, [initialDraft, isEmpty]);

  useEffect(() => {
    if (introRevealed) return;
    if (!isEmpty) {
      setIntroRevealed(true);
      return;
    }
    // Reduced motion gets the content immediately: the delay exists to make an
    // animation legible, and with no animation it is just a wait.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setIntroRevealed(true);
      return;
    }
    const timer = window.setTimeout(() => setIntroRevealed(true), 650);
    return () => window.clearTimeout(timer);
  }, [introRevealed, isEmpty]);

  useEffect(() => {
    // Nothing has scrolled yet on an empty conversation, and scrolling the
    // greeting out of view is the opposite of helpful.
    if (isEmpty) return;
    endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [messages.length, isThinking, isEmpty]);

  /**
   * Keep the caret in the composer.
   *
   * Focused on arrival so the consumer can start typing without hunting for
   * the field, and re-focused whenever a turn finishes — a conversation is a
   * back-and-forth, and having to click the box again after every answer is
   * friction on the one action this screen exists for.
   *
   * Waits for the intro reveal: focusing a field the eye has not reached yet
   * scrolls it into view and undoes the reveal that was meant to be read.
   */
  useEffect(() => {
    if (!canSend || isThinking || !introRevealed) return;
    textareaRef.current?.focus();
  }, [canSend, isThinking, introRevealed]);

  // Grow the composer with its content instead of shipping a permanently
  // oversized box: it starts one line tall and stops at roughly six.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [value]);

  /**
   * Hand the message over, with the token when one was needed.
   *
   * The token is NOT kept. Turnstile tokens are single-use server-side — a
   * replay is refused as timeout-or-duplicate — and in the ordinary case this
   * is the last message that needs one anyway: the conversation now exists and
   * every later turn authenticates with the guest token. Should this send
   * fail, the dialog opens again and the widget issues a fresh one.
   */
  const dispatch = (trimmed: string, token?: string): void => {
    // THE conversion. Fired here rather than in the parent's onSend so it
    // cannot be bypassed by a future second caller, and self-de-duped per
    // session so only the FIRST message of a visit counts. Before setValue so
    // a throwing tracker cannot leave the composer half-cleared.
    trackRfqFirstMessageSent(trimmed.length);
    setValue("");
    onSend(trimmed, token);
  };

  const send = (text: string): void => {
    const trimmed = text.trim();
    if (!trimmed || !canSend || isThinking) return;

    /*
      THE CHALLENGE IS ASKED FOR HERE, on the message that will mint a guest
      identity, and nowhere else. The composer is NOT disabled while it is
      outstanding: the visitor types and presses send exactly as they would
      otherwise, and the dialog is the answer to that press.

      Keeping the text in `value` until the dialog resolves is deliberate — a
      composer cleared into a dialog the visitor then dismisses has eaten what
      they wrote.
    */
    if (requireCaptcha && turnstileEnabled) {
      setCaptchaFailed(false);
      setPendingMessage(trimmed);
      return;
    }
    dispatch(trimmed);
  };

  /** The challenge passed — send the message it was opened for. */
  const onCaptchaSolved = (token: string): void => {
    const trimmed = pendingMessage;
    setPendingMessage(null);
    if (trimmed === null) return;
    dispatch(trimmed, token);
  };

  const closeCaptcha = (): void => {
    setPendingMessage(null);
    setCaptchaFailed(false);
    // The widget is unmounted with the dialog, so the next open starts a fresh
    // challenge rather than resuming a half-finished one.
    turnstileRef.current?.reset();
  };

  return (
    /**
     * A full-height column, not a stack that grows downward.
     *
     * The step hides the wizard footer and hands this component the rest of
     * the viewport, so the composer sits at the bottom edge where a chat
     * composer belongs and the transcript scrolls above it. Previously the
     * whole thing hugged the top and left the lower two-thirds of the page
     * empty, with the input floating in the middle of nowhere.
     *
     * `min-h-0` on both the column and the scroller is what actually permits
     * the scroll: a flex child defaults to `min-height: auto`, which refuses
     * to shrink below its content and pushes the composer off the bottom
     * instead of overflowing the transcript.
     */
    <div className="flex h-full min-h-0 w-full flex-col">
      <div
        className="min-h-0 flex-1 space-y-4 overflow-y-auto"
        role="log"
        aria-live="polite"
        aria-label={t("requests.user.quoteIntakeConversationLabel")}
      >
        {/* One fixed greeting. Swapping it for a shorter line once the
            conversation starts rewrote a message the consumer had already
            replied to — a transcript must not change behind them. */}
        {introRevealed ? (
          <div className="chat-reveal">
            <GreetingCard>{t("requests.user.quoteIntakeGreeting")}</GreetingCard>
          </div>
        ) : (
          <GreetingCard>
            <TypingDots label={t("requests.user.quoteIntakeThinking")} />
          </GreetingCard>
        )}

        {messages.map((message, index) =>
          message.role === "assistant" ? (
            <AssistantBubble key={`${message.at}-${index}`}>{message.content}</AssistantBubble>
          ) : (
            <div key={`${message.at}-${index}`} className="flex justify-end">
              <p className="max-w-[44rem] whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-sm text-primary-foreground">
                {message.content}
              </p>
            </div>
          ),
        )}

        {isThinking ? (
          <AssistantBubble>
            <TypingDots label={t("requests.user.quoteIntakeThinking")} />
          </AssistantBubble>
        ) : null}
        <div ref={endRef} />
      </div>

      {/* The bottom group: chips directly above the composer, both held at the
          bottom of the column while the transcript scrolls above them.
          `shrink-0` so a long conversation never squeezes the one row the
          consumer has to type into. */}
      <div className="shrink-0 space-y-3 pt-3">
        {/* Tinted rather than another white card. The greeting bubble, these
            chips and the composer were all bg-card with the same border and the
            same vertical rhythm, so the one row you can actually TAP read as a
            third row of content. `text-primary` carries the whole theme: navy on
            light, mint on dark, actionable in both — and deliberately not the
            teal accent, which reads as "already selected". */}
        {isEmpty && canSend && !isThinking && introRevealed ? (
          <div className="flex flex-wrap gap-2.5">
            {SUGGESTION_KEYS.map((key, index) => (
              <button
                key={key}
                type="button"
                onClick={() => send(t(key))}
                // Staggered so they read as three separate choices appearing,
                // not one block sliding in.
                style={{ animationDelay: `${80 + index * 70}ms` }}
                className="chat-reveal rounded-full border border-primary/25 bg-primary/[0.04] px-3.5 py-2 text-sm font-medium text-primary shadow-sm transition-colors hover:border-primary/50 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-primary/40 dark:bg-primary/10 dark:hover:bg-primary/20"
              >
                {t(key)}
              </button>
            ))}
          </div>
        ) : null}

        {canSend ? (
          <div
            className={cn("space-y-2", isEmpty && "chat-reveal")}
            style={isEmpty ? { animationDelay: "300ms" } : undefined}
          >
            {/* The composer is one field, not a field plus a detached button:
                the send control belongs inside the box people are typing in. */}
            <div className="flex items-end gap-2 rounded-2xl border border-border bg-card p-2 shadow-sm focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-ring/20">
              <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sends, Shift+Enter breaks the line — the convention
                  // every chat surface uses, and the one people try first.
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(value);
                  }
                }}
                placeholder={t("requests.user.quoteIntakePlaceholder")}
                rows={1}
                maxLength={MAX_MESSAGE_LENGTH}
                disabled={isThinking}
                aria-label={t("requests.user.quoteIntakePlaceholder")}
                className="max-h-40 flex-1 resize-none bg-transparent px-2 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              />
              <Button
                type="button"
                size="icon"
                onClick={() => send(value)}
                disabled={isThinking || value.trim().length === 0}
                aria-label={t("requests.user.quoteIntakeSend")}
                className="h-9 w-9 shrink-0 rounded-full"
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
            </div>

            {/* The opt-out moved UP, onto the wizard's Volver row: it is an
                escape, not a step, and under the composer it was the last
                thing on the page competing with the one action that matters.
                See user-new-request-wizard-page.tsx. */}
            {!isEmpty && turnsRemaining <= 3 ? (
              <div className="flex justify-end px-1">
                <p className="text-xs text-muted-foreground">
                  {t("requests.user.quoteIntakeTurnsLeft", { count: turnsRemaining })}
                </p>
              </div>
            ) : null}

            {/* THE OPENING STATE ONLY, which departs from ChatGPT, Claude and
                Gemini — they keep this line under the composer for the whole
                session. They are right for their shape: there, every turn IS
                the deliverable. Here the chat asks questions and the thing the
                consumer ACTS on is the draft, so the caveat earns its place
                before they commit to the conversation and again on the review
                step, and in between it is chrome under the only control that
                matters.

                Removing it here is only safe BECAUSE the review step carries
                it. If that line ever goes, this one has to come back. */}
            {isEmpty ? (
              <p className="px-1 text-center text-xs text-muted-foreground">
                {t("requests.user.quoteIntakeDisclaimer")}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/*
        THE CHALLENGE, WITH A REASON ATTACHED.

        It opens on the first send of an anonymous visit and closes the moment
        it passes, carrying that message with it. The copy exists because a
        bare Cloudflare box asks for compliance without saying what for: this
        one message creates the conversation, and that is a thing we pay for
        per turn.
      */}
      <Dialog
        open={pendingMessage !== null}
        onOpenChange={(next) => {
          if (!next) closeCaptcha();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("requests.user.quoteIntakeCaptchaTitle")}</DialogTitle>
            <DialogDescription>
              {t("requests.user.quoteIntakeCaptchaBody")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-[70px] flex-col items-center gap-3">
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={onCaptchaSolved}
              /*
                NO onExpire HANDLER, deliberately. Expiry mattered while a
                token sat in state going stale; here the only token that exists
                is the one onSuccess hands straight to the send, and the widget
                refreshes an expired challenge by itself.

                onError is the one that needs saying. The widget's own box
                reads "no es posible conectarse al sitio web", which names
                Cloudflare's problem and offers the visitor nothing — so this
                puts a way forward next to it.
              */
              onError={() => setCaptchaFailed(true)}
              options={{
                language: i18n.language.startsWith("es") ? "es" : "en",
                theme: "light",
              }}
            />

            {captchaFailed ? (
              <div className="space-y-2 text-center">
                <p className="text-xs text-muted-foreground">
                  {t("requests.user.quoteIntakeCaptchaFailed")}
                </p>
                {/* The one route that does NOT depend on the challenge:
                    signing in needs no captcha, only registering does. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    closeCaptcha();
                    onUseForm();
                  }}
                >
                  {t("requests.user.quoteIntakeUseForm")}
                </Button>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * No avatar column: it bought one glyph of "you are talking to an assistant"
 * and cost the block its left edge, indenting every assistant bubble 44px away
 * from the composer and the suggestions. Side alone already separates the two
 * roles, as it does in every chat surface.
 *
 * bg-card, not bg-muted: --muted is #f5f7fa and the wizard ground is the same
 * off-white, so a muted bubble had no edge at all. White + border matches the
 * composer and the suggestion chips, so the whole block reads as one material.
 *
 * Width is capped on the BUBBLE, not on the block: the composer spans the full
 * wizard column while message text stays at a readable measure, which is how
 * every chat surface handles it. Capping the block made the conversation
 * visibly narrower than its own heading.
 */
function AssistantBubble({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-start">
      <div className="max-w-[44rem] whitespace-pre-wrap rounded-2xl rounded-tl-md border border-border bg-card px-4 py-2.5 text-sm text-foreground shadow-sm">
        {children}
      </div>
    </div>
  );
}

/**
 * The opening greeting, styled as its own thing rather than as the first
 * transcript bubble.
 *
 * It is not a transcript message: it is generated client-side and never sent
 * to the backend, so giving it weight the model's own turns don't have is
 * honest rather than inconsistent.
 *
 * It needed that weight. Rendered as a plain `AssistantBubble` it sat between
 * the step heading, the suggestion chips and the composer at the same text
 * size, the same `bg-card`, the same border and the same shadow as the box
 * below it — four rows of one material, and the one carrying the actual
 * instruction read as the least of them.
 *
 * The weight comes from TYPE AND ELEVATION ONLY. An icon badge was tried and
 * removed: it read as decoration competing with the text rather than as a
 * voice, and the greeting is a thing to read, not a thing to look at. Tinting
 * is not available either — `--muted` is #f5f7fa in light, which IS the wizard
 * ground, and #1f2937 in dark, which is `--card` exactly, and the suggestion
 * chips already hold `primary/[0.04]`.
 */
function GreetingCard({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-start">
      {/* `dark:shadow-black/20` follows the convention the wizard panels in
          index.css already use: in dark, --card and --muted are the same
          #1f2937, so elevation is the only thing lifting a panel off the
          ground and the default shadow is too faint to do it. */}
      <div className="max-w-[44rem] whitespace-pre-wrap rounded-2xl rounded-tl-md border border-border bg-card px-4 py-3.5 text-[0.9375rem] leading-relaxed text-foreground shadow-md dark:shadow-black/20">
        {children}
      </div>
    </div>
  );
}

/** Shared by the opening reveal and by every in-flight turn. */
function TypingDots({ label }: { label: string }): JSX.Element {
  return (
    <span className="flex items-center gap-1.5 py-1" aria-label={label}>
      <Dot className="[animation-delay:-300ms]" />
      <Dot className="[animation-delay:-150ms]" />
      <Dot />
    </span>
  );
}

function Dot({ className }: { className?: string }): JSX.Element {
  return (
    <span
      className={cn(
        "h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/70 motion-reduce:animate-none",
        className,
      )}
      aria-hidden
    />
  );
}
